"use client";

import { memo, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import {
  generateEventSchema,
  getImageSrc,
  type SchemaEventData,
} from "@/utils/schemaUtils";

import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/shared/UserAvatar";

type InterestedUser = {
  user_id: string;
  profiles: {
    avatar_url: string | null;
    username: string | null;
  };
};

type EventArtist = {
  is_main_artist: boolean;
  artists: {
    id: number;
    artist: string;
    slug: string;
  };
};

interface EventCardProps {
  event: {
    id: string;
    slug: string;
    title: string;
    venue: string;
    city: string;
    date: string;
    img_url: string | null;
    description: string | null;
    assignedCitySlug: string;
    main_artist?: string;
    interested?: InterestedUser[];
    event_artists?: EventArtist[];
    event_type?: string;
    distance?: number;
    state?: string;
    end_date: string | null;
  };
  citySlug: string;
}

const EventCard = memo(({ event, citySlug }: EventCardProps) => {
  const interestedUsers = event.interested || [];
  const href = `/${event.assignedCitySlug}/${event.slug}`;
  const isDistant = event.distance && event.distance > 60;

  // Memoize expensive computations
  const { mainArtist, eventTitle, jsonLdData, imageSrc } = useMemo(() => {
    const mainArtist =
      event.main_artist ||
      event.event_artists?.find((ea) => ea.is_main_artist)?.artists.artist ||
      null;

    const eventTitle =
      mainArtist && event.title
        ? `${mainArtist} - ${event.title}`
        : mainArtist || event.title;

    const imageSrc = getImageSrc(event.img_url);

    // Create a schema-ready event object
    const schemaEvent: SchemaEventData = {
      id: event.id,
      title: event.title,
      slug: event.slug,
      venue: event.venue,
      city: event.city,
      date: event.date,
      end_date: event.end_date,
      img_url: event.img_url,
      description: event.description || null,
      main_artist: event.main_artist,
      state: event.state,
      event_artists: event.event_artists,
    };

    const jsonLdData = generateEventSchema(schemaEvent, event.assignedCitySlug);

    return {
      mainArtist,
      eventTitle,
      imageSrc,
      jsonLdData,
    };
  }, [event]);

  return (
    <article className="event-card">
      {/* Structured data for SEO */}
      {process.env.NODE_ENV === "development" && (
        <div data-testid="schema-debug" className="hidden">
          <p>Schema Context: {jsonLdData["@context"]}</p>
          <p>Schema Type: {jsonLdData["@type"]}</p>
        </div>
      )}
      <Script
        id={`event-schema-${event.id}`}
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdData) }}
      />

      <Link href={href} aria-label={`View details for ${eventTitle}`}>
        <div
          className={`group flex items-center gap-4 rounded p-1 ${
            isDistant ? "opacity-40 transition-opacity hover:opacity-100" : ""
          }`}
        >
          <div className="relative aspect-square w-20 flex-none sm:aspect-square sm:w-24">
            <Image
              src={imageSrc || "/placeholder.svg"}
              alt={eventTitle || "Event image"}
              fill
              sizes="(max-width: 640px) 5rem, 7rem"
              className="rounded-md object-cover object-center"
              loading="lazy"
              priority={false}
              quality={75}
            />
          </div>

          <div className="flex flex-1 flex-col">
            <h3 className="font-sohne text-primarygroup-hover:text-brand text-xs font-light capitalize sm:text-base">
              {eventTitle}
            </h3>
            <p className="text-xxs text-muted-foreground font-normal">
              {event.venue} - {event.city}
            </p>
            {(event.event_type === "festival" || isDistant) && (
              <div className="flex gap-1" aria-label="Event tags">
                {event.event_type === "festival" && (
                  <Badge className="border-brand bg-brand text-background px-1 py-0 text-[10px] font-medium tracking-tight">
                    {event.event_type}
                  </Badge>
                )}
                {isDistant && (
                  <Badge className="border-rose-600 bg-rose-600/10 px-1 py-0 text-[10px] font-medium tracking-tight text-rose-500">
                    {Math.round(event.distance!)} mi away
                  </Badge>
                )}
              </div>
            )}
          </div>

          {interestedUsers.length > 0 && (
            <div className="flex items-center">
              <div
                className="flex -space-x-3"
                aria-label={`${interestedUsers.length} people interested`}
              >
                {interestedUsers.slice(0, 3).map((user) => (
                  <UserAvatar
                    key={user.user_id}
                    user={{
                      id: user.user_id,
                      avatar_url: user.profiles.avatar_url,
                      username: user.profiles.username,
                    }}
                    size="sm"
                    linkToProfile={false}
                  />
                ))}
                {interestedUsers.length > 3 && (
                  <div
                    className="bg-secondary text-muted-foreground ring-background hover:text-foreground z-10 flex size-6 items-center justify-center rounded-full text-xs ring-2"
                    aria-hidden="true"
                  >
                    +{interestedUsers.length - 3}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Link>
    </article>
  );
});

EventCard.displayName = "EventCard";

export default EventCard;
